<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import { localize } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')
const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
// const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
// const FooterFullLayout = () => import('@/views/components/FooterFullLayout.vue')

export default {
  components: {
    // Layouts
    LayoutFull,
    // LayoutHorizontal,
    LayoutVertical,
    // FooterFullLayout,
  },
  setup() {
    const { skin, skinClasses, isRTL } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    if (localStorage.getItem('locale') === 'ar') isRTL.value = true

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    ...mapGetters(['loggedIn', 'getUserData']),
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      if (this.$route.meta.layout === 'vertical') return `layout-${this.contentLayoutType}`
      return 'layout-full'
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  watch: {
    // Единая точка управления WebSocket-соединением: держим его открытым, пока
    // пользователь залогинен, и через account ↔ dashboard соединение не рвётся,
    // потому что App.vue не размонтируется при смене layout'а.
    loggedIn: {
      immediate: true,
      handler(isLoggedIn) {
        if (isLoggedIn) this.connect(this.getUserData.id)
        else this.disconnect()
      },
    },
  },
  beforeCreate() {
    if (localStorage.getItem('locale') && this.$route.name !== 'booking-page') {
      localize(localStorage.getItem('locale'))
    }
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    // show alert
    window.addEventListener('show-alert', event => {
      this.activateToast(event.detail.ic, event.detail.color, event.detail.title, event.detail.message, event.detail.timeout)
    })
  },
  beforeDestroy() {
    window.removeEventListener('show-alert')
  },
  methods: {
    ...mapActions(['connect', 'disconnect']),
    activateToast(ic, color, text1, text2, timeout) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            icon: ic,
            variant: color,
            title: text1,
            text: text2,
          },
        },
        {
          timeout,
        },
      )
    },
  },
}
</script>
